import React, { useState } from 'react';
import { 
  Mail, 
  Phone, 
  Send, 
  Menu, 
  X, 
  ArrowRight, 
  Code,  
  Zap, 
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from './Zenble_Logo.png';

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const nombre = e.target.elements.nombre.value;
    const empresa = e.target.elements.empresa.value;
    const correo_electronico = e.target.elements.correo_electronico.value;
    const telefono = e.target.elements.telefono.value;
    const mensaje = e.target.elements.mensaje.value;

    // Validación rápida frontend
    if (!telefono.trim() && !correo_electronico.trim()) {
      alert("Por favor, déjanos al menos un correo o número de WhatsApp para contactarte.");
      setErrorMessage("Por favor, déjanos al menos un correo o número de WhatsApp.");
      setFormStatus('error');
      return; // Detiene el envío
    }

    setFormStatus('submitting');
    setErrorMessage('');
    
    const formData = {
      nombre,
      empresa,
      correo_electronico,
      telefono,
      mensaje,
    };

    try {
      const response = await fetch('https://n8n.zenblesolutions.com/webhook/zenble_form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Hubo un problema al enviar el formulario. Por favor, inténtalo de nuevo.');
      }

      setFormStatus('success');
      e.target.reset(); // Limpiar formulario
      
      // Volver al estado normal después de unos segundos
      setTimeout(() => setFormStatus('idle'), 5000);
    } catch (error) {
      setFormStatus('error');
      setErrorMessage(error.message || 'Error de conexión. Inténtalo más tarde.');
    }
  };

  const navLinks = [
    { name: 'Inicio', href: '#inicio' },
    { name: 'Nosotros', href: '#nosotros' },
    { name: 'Servicios', href: '#servicios' },
    { name: 'Contacto', href: '#contacto' },
  ];

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-100 scroll-smooth relative overflow-hidden">
      {/* Patrón de Cuadrícula Cyber */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.04)_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] pointer-events-none -z-10 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,#000_70%,transparent_100%)]" />

      {/* Fondo con Luces Radial-Glow Ambientales (Efecto Cyber Intensificado) */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
        <div className="absolute top-[-5%] left-[-15%] w-[70vw] h-[70vw] rounded-full bg-indigo-500/20 blur-[120px] opacity-90" />
        <div className="absolute bottom-[15%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-cyan-400/20 blur-[120px] opacity-85" />
        <div className="absolute top-[35%] left-[20%] w-[50vw] h-[50vw] rounded-full bg-fuchsia-600/15 blur-[100px] opacity-75" />
      </div>

      {/* Navegación (Floating Glass Navbar) */}
      <motion.nav 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="fixed top-4 inset-x-0 mx-auto w-[92%] max-w-7xl bg-slate-900/60 backdrop-blur-lg z-50 border border-slate-800/80 rounded-2xl shadow-xl shadow-slate-950/40"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <a href="#inicio" className="flex items-center gap-3 group">
                <img 
                  src={logo} 
                  alt="Zenble Logo" 
                  className="h-16 w-auto object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </a>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-slate-400 hover:text-white font-medium transition-colors text-lg"
                >
                  {link.name}
                </a>
              ))}
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                href="#contacto"
                className="bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-500/20 text-base"
              >
                Hablemos
              </motion.a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleMenu}
                className="text-slate-400 hover:text-white focus:outline-none p-2"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-slate-900/95 backdrop-blur-lg border-t border-slate-800 rounded-b-2xl overflow-hidden"
            >
              <div className="px-6 pt-2 pb-6 space-y-2">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-3 py-3 text-base font-medium text-slate-300 hover:text-white hover:bg-slate-800/40 rounded-xl"
                  >
                    {link.name}
                  </a>
                ))}
                <a
                  href="#contacto"
                  onClick={() => setIsMenuOpen(false)}
                  className="block text-center bg-indigo-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-indigo-500 transition-colors mt-4"
                >
                  Hablemos
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section */}
      <section id="inicio" className="relative pt-36 pb-24 lg:pt-48 lg:pb-36 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 py-2 px-5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-base md:text-lg font-semibold tracking-wide mb-8"
          >
            <Zap size={34} className="text-indigo-400" /> Innovación y Tecnología de Vanguardia
          </motion.span>

          <motion.h1 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-7xl lg:text-8xl font-extrabold tracking-tight text-white mb-8 leading-tight"
          >
            Impulsa tu negocio con <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-400 to-cyan-400">
              Zenble Solutions
            </span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl text-lg md:text-xl text-slate-400 mx-auto mb-12 leading-relaxed"
          >
            Transformamos tus ideas en soluciones digitales efectivas, robustas y escalables. 
            Te ayudamos a dominar el mundo tecnológico para que te enfoques en crecer.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              href="#contacto"
              className="w-full sm:w-auto px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-lg transition-colors shadow-lg shadow-indigo-600/35 flex items-center justify-center gap-2"
            >
              Iniciar Proyecto <ArrowRight size={20} />
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              href="#nosotros"
              className="w-full sm:w-auto px-8 py-4 bg-slate-900/60 hover:bg-slate-800/60 text-slate-200 border border-slate-800 rounded-xl font-semibold text-lg transition-colors flex items-center justify-center"
            >
              Conocer más
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Nosotros / Información de la empresa */}
      <section id="nosotros" className="py-24 bg-slate-950 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                ¿Quiénes somos en Zenble?
              </h2>
              <p className="text-lg text-slate-400 mb-6 leading-relaxed">
                En Zenble Solutions, somos un equipo multidisciplinario de estrategas, diseñadores y desarrolladores apasionados por crear productos digitales de alto impacto.
              </p>
              <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                Nuestra misión es simplificar la complejidad tecnológica y entregar herramientas que optimicen procesos, mejoren la experiencia de usuario y generen resultados medibles para tu empresa.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-slate-900/40 p-5 rounded-2xl border border-slate-800/60">
                  <p className="text-4xl font-bold text-indigo-400 mb-1">+50</p>
                  <p className="text-sm text-slate-500 font-medium">Proyectos Exitosos</p>
                </div>
                <div className="bg-slate-900/40 p-5 rounded-2xl border border-slate-800/60">
                  <p className="text-4xl font-bold text-cyan-400 mb-1">100%</p>
                  <p className="text-sm text-slate-500 font-medium">Compromiso</p>
                </div>
              </div>
            </motion.div>
            
            {/* Tarjetas de características */}
            <div className="grid gap-6 sm:grid-cols-2">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-slate-900/30 p-6 rounded-2xl border border-slate-800/80 hover:border-slate-700/60 hover:bg-slate-900/50 hover:shadow-xl hover:shadow-slate-950/20 transition-all group"
              >
                <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Code size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Desarrollo a Medida</h3>
                <p className="text-slate-400 text-sm leading-relaxed">Software creado específicamente para las necesidades únicas de tu operación.</p>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="bg-slate-900/30 p-6 rounded-2xl border border-slate-800/80 hover:border-slate-700/60 hover:bg-slate-900/50 hover:shadow-xl hover:shadow-slate-950/20 transition-all group sm:mt-8"
              >
                <div className="w-12 h-12 bg-cyan-500/10 text-cyan-400 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                  <Zap size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Rápida Ejecución</h3>
                <p className="text-slate-400 text-sm leading-relaxed">Metodologías ágiles para entregar valor funcional en el menor tiempo posible.</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Servicios Section (Placeholder para consistencia de ID) */}
      <section id="servicios" className="py-12 bg-slate-950 opacity-40">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="w-24 h-1 bg-gradient-to-r from-indigo-500 to-cyan-500 mx-auto rounded-full"></div>
        </div>
      </section>

      {/* Contacto */}
      <section id="contacto" className="py-24 bg-slate-950 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">Trabajemos Juntos</h2>
            <p className="text-lg text-slate-400">
              ¿Tienes un proyecto en mente? Déjanos tus datos y uno de nuestros asesores se pondrá en contacto contigo a la brevedad.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 lg:gap-8 items-start">
            {/* Información de Contacto */}
            <div className="lg:col-span-1 space-y-6">
              
              <div className="flex items-start gap-4 p-5 bg-slate-900/30 rounded-2xl border border-slate-800/80">
                <div className="w-12 h-12 bg-indigo-500/10 text-indigo-400 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail size={24} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-1 text-slate-200">Correo Electrónico</h4>
                  <a href="mailto:support@zenblesolutions.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">
                    support@zenblesolutions.com
                  </a>
                </div>
              </div>
              
              {/* Enlace de WhatsApp con efectos interactivos premium */}
              <motion.a 
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="https://wa.me/528124184523"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-5 bg-emerald-500/5 hover:bg-emerald-500/10 border border-emerald-500/20 hover:border-emerald-500/40 rounded-2xl transition-all duration-300 shadow-lg shadow-emerald-950/10 group"
              >
                <div className="w-12 h-12 bg-emerald-500 text-slate-950 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20 transition-transform group-hover:scale-105">
                  <Phone size={24} className="fill-current" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-1 text-emerald-400">Teléfono (WhatsApp)</h4>
                  <p className="text-slate-300 group-hover:text-white transition-colors">+52 (81) 2418-4523</p>
                </div>
              </motion.a>
            </div>

            {/* Formulario */}
            <div className="lg:col-span-2 bg-slate-900/40 rounded-3xl p-8 lg:p-10 border border-slate-800/80 shadow-2xl backdrop-blur-sm">
              <AnimatePresence mode="wait">
                {formStatus === 'success' ? (
                  <motion.div 
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="h-full flex flex-col items-center justify-center text-center space-y-4 py-12"
                  >
                    <div className="w-20 h-20 bg-green-500/10 text-green-400 rounded-full flex items-center justify-center mb-4 border border-green-500/20">
                      <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-2xl font-bold">¡Mensaje Enviado!</h3>
                    <p className="text-slate-400 max-w-md">
                      Gracias por contactar a Zenble Solutions. Hemos recibido tu mensaje y te responderemos muy pronto.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="nombre" className="text-sm font-medium text-slate-300">
                          Nombre Completo
                        </label>
                        <input
                          type="text"
                          id="nombre"
                          name="nombre"
                          required
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                          placeholder="Ej. Juan Pérez"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="empresa" className="text-sm font-medium text-slate-300">
                          Empresa (Opcional)
                        </label>
                        <input
                          type="text"
                          id="empresa"
                          name="empresa"
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                          placeholder="Tu compañía"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="correo_electronico" className="text-sm font-medium text-slate-300">
                          Correo Electrónico
                        </label>
                        <input
                          type="email"
                          id="correo_electronico"
                          name="correo_electronico"
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                          placeholder="correo@ejemplo.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="telefono" className="text-sm font-medium text-slate-300">
                          Teléfono / WhatsApp
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all"
                          placeholder="Ej. +52 81 2418 4523"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="mensaje" className="text-sm font-medium text-slate-300">
                        ¿En qué podemos ayudarte?
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        required
                        rows={4}
                        className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3.5 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-transparent transition-all resize-none"
                        placeholder="Describe brevemente tu proyecto o necesidad..."
                      ></textarea>
                    </div>

                    <AnimatePresence>
                      {formStatus === 'error' && (
                        <motion.div 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="p-4 bg-rose-500/10 border border-rose-500/20 text-rose-300 rounded-xl text-sm flex items-center gap-3"
                        >
                          <AlertCircle size={20} className="text-rose-400 flex-shrink-0" />
                          <span>{errorMessage}</span>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <motion.button
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                        formStatus === 'submitting' 
                          ? 'bg-indigo-900 text-indigo-300 cursor-not-allowed' 
                          : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/25'
                      }`}
                    >
                      {formStatus === 'submitting' ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Enviando...
                        </>
                      ) : (
                        <>
                          Enviar Mensaje <Send size={20} />
                        </>
                      )}
                    </motion.button>
                  </form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12 border-t border-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <img 
              src={logo} 
              alt="Zenble Logo" 
              className="h-8 w-auto object-contain"
            />
            <span className="font-bold text-lg text-white">Zenble.</span>
          </div>
          
          <p className="text-slate-500 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} Zenble Solutions. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}